import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Building, DollarSign, MapPin, CheckCircle, Loader, Upload, Calendar } from 'lucide-react';
import { formatCurrency } from '../../utils/calculations';
interface PropertyManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  property?: {
    id: string;
    address: string;
    city: string;
    rentAmount: number;
    bedrooms: number;
    bathrooms: number;
  };
  onSuccess: () => void;
}
type Step = 'details' | 'processing' | 'success';
export function PropertyManagementModal({
  isOpen,
  onClose,
  mode,
  property,
  onSuccess
}: PropertyManagementModalProps) {
  const [step, setStep] = useState<Step>('details');
  const [formData, setFormData] = useState({
    address: property?.address || '',
    city: property?.city || '',
    rentAmount: property?.rentAmount || 0,
    bedrooms: property?.bedrooms || 1,
    bathrooms: property?.bathrooms || 1,
    propertyType: 'apartment',
    description: ''
  });
  const handleSubmit = () => {
    setStep('processing');
    // Simulate API call
    setTimeout(() => {
      setStep('success');
      setTimeout(() => {
        onSuccess();
        onClose();
        resetModal();
      }, 2000);
    }, 2000);
  };
  const resetModal = () => {
    setStep('details');
    if (!property) {
      setFormData({
        address: '',
        city: '',
        rentAmount: 0,
        bedrooms: 1,
        bathrooms: 1,
        propertyType: 'apartment',
        description: ''
      });
    }
  };
  const handleClose = () => {
    if (step !== 'processing') {
      onClose();
      setTimeout(resetModal, 300);
    }
  };
  return <AnimatePresence data-id="element-1688">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" data-id="element-1689" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1690">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-1691">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10" data-id="element-1692">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-1693">
                  {step === 'success' ? 'Property Added!' : mode === 'add' ? 'Add New Property' : 'Edit Property'}
                </h2>
                {step !== 'processing' && <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1694">
                    <X className="h-5 w-5 text-gray-500" data-id="element-1695" />
                  </button>}
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-1696">
                {step === 'details' && <div className="space-y-6" data-id="element-1697">
                    {/* Property Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1698">
                      <div className="md:col-span-2" data-id="element-1699">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1700">
                          Property Address *
                        </label>
                        <div className="relative" data-id="element-1701">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1702" />
                          <input type="text" value={formData.address} onChange={e => setFormData({
                      ...formData,
                      address: e.target.value
                    })} placeholder="e.g., 12 Admiralty Way, Lekki Phase 1" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1703" />
                        </div>
                      </div>

                      <div data-id="element-1704">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1705">
                          City *
                        </label>
                        <select value={formData.city} onChange={e => setFormData({
                    ...formData,
                    city: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1706">
                          <option value="" data-id="element-1707">Select City</option>
                          <option value="Lagos" data-id="element-1708">Lagos</option>
                          <option value="Abuja" data-id="element-1709">Abuja</option>
                          <option value="Port Harcourt" data-id="element-1710">Port Harcourt</option>
                          <option value="Ibadan" data-id="element-1711">Ibadan</option>
                          <option value="Kano" data-id="element-1712">Kano</option>
                        </select>
                      </div>

                      <div data-id="element-1713">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1714">
                          Property Type *
                        </label>
                        <select value={formData.propertyType} onChange={e => setFormData({
                    ...formData,
                    propertyType: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1715">
                          <option value="apartment" data-id="element-1716">Apartment</option>
                          <option value="house" data-id="element-1717">House</option>
                          <option value="duplex" data-id="element-1718">Duplex</option>
                          <option value="bungalow" data-id="element-1719">Bungalow</option>
                        </select>
                      </div>

                      <div data-id="element-1720">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1721">
                          Monthly Rent *
                        </label>
                        <div className="relative" data-id="element-1722">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium" data-id="element-1723">
                            ₦
                          </span>
                          <input type="number" value={formData.rentAmount} onChange={e => setFormData({
                      ...formData,
                      rentAmount: Number(e.target.value)
                    })} placeholder="150000" className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1724" />
                        </div>
                      </div>

                      <div data-id="element-1725">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1726">
                          Bedrooms *
                        </label>
                        <input type="number" value={formData.bedrooms} onChange={e => setFormData({
                    ...formData,
                    bedrooms: Number(e.target.value)
                  })} min={1} max={10} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1727" />
                      </div>

                      <div data-id="element-1728">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1729">
                          Bathrooms *
                        </label>
                        <input type="number" value={formData.bathrooms} onChange={e => setFormData({
                    ...formData,
                    bathrooms: Number(e.target.value)
                  })} min={1} max={10} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1730" />
                      </div>

                      <div className="md:col-span-2" data-id="element-1731">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1732">
                          Description
                        </label>
                        <textarea value={formData.description} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} placeholder="Describe the property features, amenities, etc." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1733" />
                      </div>
                    </div>

                    {/* Property Images */}
                    <div data-id="element-1734">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1735">
                        Property Images
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors cursor-pointer" data-id="element-1736">
                        <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-1737" />
                        <p className="text-sm text-gray-600 mb-2" data-id="element-1738">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-1739">
                          PNG, JPG up to 10MB (Max 5 images)
                        </p>
                      </div>
                    </div>

                    {/* Expected Returns */}
                    {formData.rentAmount > 0 && <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200" data-id="element-1740">
                        <h3 className="font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1741">
                          <DollarSign className="h-5 w-5 text-green-600 mr-2" data-id="element-1742" />
                          Expected Annual Income
                        </h3>
                        <div className="grid grid-cols-2 gap-4" data-id="element-1743">
                          <div data-id="element-1744">
                            <p className="text-sm text-gray-600 mb-1" data-id="element-1745">
                              Monthly Rent
                            </p>
                            <p className="text-lg font-bold text-gray-900" data-id="element-1746">
                              {formatCurrency(formData.rentAmount)}
                            </p>
                          </div>
                          <div data-id="element-1747">
                            <p className="text-sm text-gray-600 mb-1" data-id="element-1748">
                              Annual Income
                            </p>
                            <p className="text-lg font-bold text-green-600" data-id="element-1749">
                              {formatCurrency(formData.rentAmount * 12)}
                            </p>
                          </div>
                        </div>
                      </div>}

                    {/* Action Buttons */}
                    <div className="flex gap-3" data-id="element-1750">
                      <button onClick={handleClose} className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1751">
                        Cancel
                      </button>
                      <button onClick={handleSubmit} disabled={!formData.address || !formData.city || formData.rentAmount === 0} className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center" data-id="element-1752">
                        <Building className="h-5 w-5 mr-2" data-id="element-1753" />
                        {mode === 'add' ? 'Add Property' : 'Save Changes'}
                      </button>
                    </div>
                  </div>}

                {step === 'processing' && <div className="py-12 text-center" data-id="element-1754">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6" data-id="element-1755">
                      <Loader className="h-8 w-8 text-blue-600 animate-spin" data-id="element-1756" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1757">
                      {mode === 'add' ? 'Adding Property...' : 'Saving Changes...'}
                    </h3>
                    <p className="text-gray-600" data-id="element-1758">
                      Please wait while we process your request.
                    </p>
                  </div>}

                {step === 'success' && <div className="py-12 text-center" data-id="element-1759">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200,
                damping: 15
              }} className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6" data-id="element-1760">
                      <CheckCircle className="h-8 w-8 text-green-600" data-id="element-1761" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1762">
                      {mode === 'add' ? 'Property Added Successfully!' : 'Changes Saved!'}
                    </h3>
                    <p className="text-gray-600 mb-6" data-id="element-1763">
                      {mode === 'add' ? 'Your property has been added to your portfolio.' : 'Your property details have been updated.'}
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto" data-id="element-1764">
                      <p className="text-sm text-blue-800" data-id="element-1765">
                        {mode === 'add' ? 'You can now start receiving tenant applications for this property.' : 'The updated details are now visible to potential tenants.'}
                      </p>
                    </div>
                  </div>}
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}